import React from "react";
import {
  FaCheck,
  FaCrown,
  FaCalendarAlt,
  FaCreditCard,
  FaFileContract,
  FaTag,
  FaShoppingBag,
  FaReceipt,
  FaInfinity,
  FaPercentage,
  FaDollarSign,
  FaBuilding,
} from "react-icons/fa";
import { formatDate } from "utils/helperFunctions";

export default function ProductView({ product }) {
  if (!product) return null;

  const getCategoryIcon = (category) => {
    const iconMap = {
      campaign: FaTag,
      social: FaShoppingBag,
      community: FaShoppingBag,
      gift: FaShoppingBag,
      sms: FaReceipt,
      marketing: FaTag,
      other: FaShoppingBag,
    };
    return iconMap[category] || FaShoppingBag;
  };

  const getCategoryColor = (category) => {
    const colorMap = {
      campaign: "bg-purple-100 text-purple-800",
      social: "bg-blue-100 text-blue-800",
      community: "bg-green-100 text-green-800",
      gift: "bg-pink-100 text-pink-800",
      sms: "bg-orange-100 text-orange-800",
      marketing: "bg-red-100 text-red-800",
      other: "bg-lightgray text-darkergray",
    };
    return colorMap[category] || "bg-lightgray text-darkergray";
  };

  const getTypeColor = (type) => {
    return type === "subscription"
      ? "bg-indigo-100 text-indigo-800"
      : "bg-emerald-100 text-emerald-800";
  };

  const formatContractorFee = (contractorFee) => {
    if (!contractorFee) return "Not set";

    if (contractorFee.type === "percentage") {
      return `${contractorFee.value}%`;
    } else {
      return `$${contractorFee.value.toFixed(2)}`;
    }
  };

  const CategoryIcon = getCategoryIcon(product.category);

  return (
    <div className="bg-white">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="flex items-center gap-2">
              {product.name}

              {product.isPopular && (
                <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1">
                  <FaCrown size={12} />
                  Popular
                </span>
              )}
            </h4>
            {product.shortName ? (
              <p className="mt-1 text-sm font-medium text-darkgray">
                Short Name: {product.shortName}{" "}
              </p>
            ) : (
              ""
            )}
            <p className="text-darkergray mt-1">{product.description}</p>
            <div className="flex items-center gap-3 mt-3">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${getTypeColor(
                  product.type,
                )}`}
              >
                {product.type.charAt(0).toUpperCase() + product.type.slice(1)}
              </span>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${getCategoryColor(
                  product.category,
                )}`}
              >
                <CategoryIcon size={12} />
                {product.category.charAt(0).toUpperCase() +
                  product.category.slice(1)}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold ">
              {product.priceDisplay ||
                (product.price ? `$${product.price}` : "Custom")}
            </div>
            {product.type === "subscription" && product.billingCycle && (
              <div className="mt-1 text-sm text-darkergray capitalize flex items-center gap-1 justify-end">
                <FaCalendarAlt size={12} />
                {product.billingCycle}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-mediumgray">
        <dl className="divide-y divide-mediumgray">
          {/* Basic Information */}
          <div className="bg-lightergray px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium ">Product ID</dt>
            <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2 font-mono">
              {product._id}
            </dd>
          </div>

          <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium ">Product Name</dt>
            <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2 font-semibold">
              {product.name}
            </dd>
          </div>

          <div className="bg-lightergray px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium ">Description</dt>
            <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
              <pre className="whitespace-pre-wrap font-sans">
                {product.description}
              </pre>
            </dd>
          </div>

          {/* Product Type & Category */}
          <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium ">Product Type</dt>
            <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
              <span
                className={`px-3 py-1 uppercase leading-wide rounded-full text-sm font-medium ${getTypeColor(
                  product.type,
                )}`}
              >
                {product.type}
              </span>
            </dd>
          </div>

          <div className="bg-lightergray px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium ">Category</dt>
            <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 w-fit ${getCategoryColor(
                  product.category,
                )}`}
              >
                <CategoryIcon size={12} />
                {product.category.charAt(0).toUpperCase() +
                  product.category.slice(1)}
              </span>
            </dd>
          </div>

          {/* Linked Campaign Event */}
          {product.campaignEventId && (
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium">Linked Campaign Event</dt>
              <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
                <div className="space-y-1">
                  <span className="font-semibold">
                    {product.campaignEventId.name}
                  </span>
                  {product.campaignEventId.eventTypeId && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {product.campaignEventId.eventTypeId.category}
                      </span>
                      {product.campaignEventId.eventTypeId
                        .requiresContractor && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          Contractor:{" "}
                          {
                            product.campaignEventId.eventTypeId
                              .contractorService
                          }
                        </span>
                      )}
                    </div>
                  )}
                  {product.campaignEventId.durationHours && (
                    <p className="text-darkgray text-xs mt-1">
                      Duration: {product.campaignEventId.durationHours}h
                      {product.campaignEventId.quantity > 1 &&
                        ` • Qty: ${product.campaignEventId.quantity}`}
                      {product.campaignEventId.size &&
                        ` • Size: ${product.campaignEventId.size}`}
                    </p>
                  )}
                </div>
              </dd>
            </div>
          )}

          {/* Pricing Information */}
          <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium ">Price</dt>
            <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
              <div className="flex items-center gap-4">
                <span className="text-lg font-semibold">
                  {product.price ? `$${product.price}` : "Custom Pricing"}
                </span>
                {product.priceDisplay && (
                  <span className="text-sm text-darkgray bg-lightgray px-2 py-1 rounded">
                    Display: {product.priceDisplay}
                  </span>
                )}
              </div>
            </dd>
          </div>

          {/* Print Metadata — size token / run quantity */}
          {(product.metadata?.size || product.metadata?.quantity) && (
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium ">Print Details</dt>
              <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
                <div className="flex items-center gap-3">
                  {product.metadata?.size && (
                    <span className="bg-lightgray px-3 py-1 rounded-full text-sm">
                      Size: {product.metadata.size}
                    </span>
                  )}
                  {product.metadata?.quantity != null && (
                    <span className="bg-lightgray px-3 py-1 rounded-full text-sm">
                      Quantity: {product.metadata.quantity}
                    </span>
                  )}
                </div>
              </dd>
            </div>
          )}

          {/* Contractor Fee */}
          <div className="bg-lightergray px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium ">Contractor Fee</dt>
            <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
              {product.contractorFee ? (
                <div className="flex items-center gap-3">
                  <span className="text-lg font-semibold text-indigo-700">
                    {formatContractorFee(product.contractorFee)}
                  </span>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                      product.contractorFee.type === "percentage"
                        ? "bg-purple-100 text-purple-800"
                        : "bg-teal-100 text-teal-800"
                    }`}
                  >
                    {product.contractorFee.type === "percentage" ? (
                      <>
                        <FaPercentage size={10} />
                        Percentage
                      </>
                    ) : (
                      <>
                        <FaDollarSign size={10} />
                        Fixed
                      </>
                    )}
                  </span>
                </div>
              ) : (
                <span className="text-darkgray italic">Not set</span>
              )}
            </dd>
          </div>

          {/* Contractor Company */}
          {product.contractorCompanyId && (
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium ">Contractor Company</dt>
              <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
                <span className="flex items-center gap-2 font-semibold w-fit">
                  <FaBuilding className="text-indigo-500" size={14} />
                  {product?.contractorCompanyId?.name}
                </span>
              </dd>
            </div>
          )}

          {/* Owner — a company that added this product itself, from their
              profile tab. Catalogue products have no owner and are offered
              to everyone. */}
          {product.companyId && (
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium ">Owner</dt>
              <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
                <span className="flex items-center gap-2 font-semibold w-fit">
                  <FaBuilding className="text-purple-500" size={14} />
                  {product.companyId.name}
                </span>
              </dd>
            </div>
          )}

          {/* Available To — which offices can see/buy this product */}
          <div className="bg-lightergray px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium ">Available To</dt>
            <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
              {product.companyId ? (
                // A company's own product never reaches another company,
                // whatever the office list says.
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                  {product.companyId.name} only
                </span>
              ) : product.offices?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {product.offices.map((o) => (
                    <span
                      key={o?._id || o}
                      className="bg-lightgray px-3 py-1 rounded-full text-sm"
                    >
                      {o?.name || o}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                  All offices
                </span>
              )}
            </dd>
          </div>

          {/* Billing Cycle - Only for subscriptions */}
          {product.type === "subscription" && product.billingCycle && (
            <div className="bg-lightergray px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium ">Billing Cycle</dt>
              <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
                <span className="capitalize bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  {product.billingCycle}
                </span>
              </dd>
            </div>
          )}

          {/* Usage Limits - Only for subscriptions */}
          {product.type === "subscription" && (
            <>
              {product.maxCampaigns !== null &&
                product.maxCampaigns !== undefined && (
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium">Campaign Limit</dt>
                    <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 w-fit ${
                          product.maxCampaigns === -1
                            ? "bg-green-100 text-green-800"
                            : "bg-orange-100 text-orange-800"
                        }`}
                      >
                        {product.maxCampaigns === -1 ? (
                          <>
                            <FaInfinity size={12} />
                            Unlimited
                          </>
                        ) : (
                          `${product.maxCampaigns} campaigns`
                        )}
                      </span>
                    </dd>
                  </div>
                )}
            </>
          )}

          {/* Features */}
          <div className="bg-lightergray px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium ">Features</dt>
            <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
              {product.features && product.features.length > 0 ? (
                <ul className="space-y-2">
                  {product.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <FaCheck
                        className="text-green-500 mt-1 flex-shrink-0"
                        size={12}
                      />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="text-darkgray italic">
                  No features defined
                </span>
              )}
            </dd>
          </div>

          {/* Terms */}
          <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium ">Terms & Conditions</dt>
            <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
              {product.terms && product.terms.length > 0 ? (
                <ul className="space-y-2">
                  {product.terms.map((term, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <FaFileContract
                        className="text-blue-500 mt-1 flex-shrink-0"
                        size={12}
                      />
                      <span>{term}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="text-darkgray italic">No terms defined</span>
              )}
            </dd>
          </div>

          {/* Stripe Integration */}
          {product.stripePriceId && (
            <div className="bg-lightergray px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium ">Stripe Integration</dt>
              <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
                <div className="flex items-center gap-2">
                  <FaCreditCard className="text-blue-500" size={14} />
                  <span className="font-mono bg-lightgray px-2 py-1 rounded text-xs">
                    {product.stripePriceId}
                  </span>
                </div>
              </dd>
            </div>
          )}

          {/* Free Inclusion */}
          {product.isFreeInclusion && (
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium ">Free Inclusion</dt>
              <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
                {product.freeWithCategories?.length ? (
                  <>
                    Added free once the same contractor company has one of these
                    in the cart:{" "}
                    <span className="font-medium">
                      {product.freeWithCategories.join(", ")}
                    </span>
                  </>
                ) : (
                  <span className="text-darkgray italic">
                    No trigger categories set — this never fires.
                  </span>
                )}
              </dd>
            </div>
          )}

          {/* Status */}
          <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium ">Status</dt>
            <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
              <div className="flex items-center gap-4">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    product.isPopular
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-lightgray text-darkergray"
                  }`}
                >
                  {product.isPopular ? "Popular Product" : "Standard Product"}
                </span>
                {product.isSystem && (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    System
                  </span>
                )}
              </div>
            </dd>
          </div>

          {/* Timestamps */}
          <div className="bg-lightergray px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium ">Created At</dt>
            <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
              {product.createdAt
                ? formatDate(product.createdAt)
                : "Not available"}
            </dd>
          </div>

          <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium ">Last Updated</dt>
            <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
              {product.updatedAt
                ? formatDate(product.updatedAt)
                : "Not available"}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
